import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Checkbox,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  DialogTitle,
  FormControlLabel,
  Button,
  Dialog,
  DialogContent,
  Alert,
  AlertTitle,
  DialogActions,
  Grid,
  alpha,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { useTranslation } from "react-i18next";
import { UserContext } from "../../App";
import { deleteData, fetchData, fetchDataBlob } from "../../utils/api";
import AnalyzeClassification from "./AnalyzeClassification";
import AnalyzeRegression from "./AnalyzeRegression";
import AnalyzeForecast from "./AnalyzeForecast";
import AnalyzeAFS from "./AnalyzeAFS";
import AnalyzeErrorModel from "./AnalyzeErrorModel";

export default function Analyzer() {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();

  // get data from current dataset + Experiment
  const {
    datasets,
    currentDatasetIdx,
    experiments,
    currentExperimentIdx,
    currentExperimentProgress,
  } = useContext(UserContext);

  const [selectedParentRun, setSelectedParentRun] = useState("");
  const [selectedRun, setSelectedRun] = useState("");
  const [runsFamilyData, setRunsFamilyData] = useState([]);
  const [childRunsNames, setChildRunsNames] = useState([]);
  const [targetNames, setTargetNames] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState("");
  const [runData, setRunData] = useState({
    metrics: {},
    predictionDetails: { classNames: [] },
    params: { stepName: "" },
  });
  const [open, setOpen] = useState(false);
  const [downloadModelClicked, setDownloadModelClicked] = React.useState(false);
  const [openDownload, setOpenDownload] = React.useState(false);
  const [includeValidation, setIncludeValidation] = React.useState(false);
  const [includeTrain, setIncludeTrain] = React.useState(false);
  const [includeOriginal, setIncludeOriginal] = useState(false);
  const [includeSplit, setIncludeSplit] = useState(false);
  const [includeLinuxPythonEnv, setIncludeLinuxPythonEnv] =
    React.useState(false);
  const [includeWindowsPythonEnv, setIncludeWindowsPythonEnv] =
    React.useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchRun = (runName) => {
    setIsLoading(true);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/run/${runName}`;

    fetchData(url).then((data) => {
      setRunData(data);

      // Extract target names from runData itself
      let targets = [];
      if (data.predictionDetails.targetName) {
        targets = [data.predictionDetails.targetName]; // single target
      } else if (data.predictionDetails?.targetNames?.length) {
        targets = data.predictionDetails.targetNames; // multiple targets
      }
      setTargetNames(targets);
      setSelectedTarget(targets[0] || "");
      setIsLoading(false);
    });
  };

  const fetchRuns = () => {
    setIsLoading(true);
    fetchData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/runs`
    ).then((data) => {
      setRunsFamilyData(data.runs);

      // if there is only one run, set it as selected
      if (data.runs.length === 1) {
        setSelectedParentRun(data.runs.at(0).parent.runName);
        setChildRunsNames(
          data.runs.at(0).children.map((child) => child.runName)
        );
        // set last child run as selected
        const lastChildRun = data.runs
          .at(0)
          .children.at(data.runs.at(0).children.length - 1);
        fetchRun(lastChildRun.runName);
        setSelectedRun(lastChildRun.runName);
      }
      setIsLoading(false);
    });
  };

  const handleOpenDownloadSelecter = () => setOpenDownload(true);

  const handleCloseDownloadSelecter = () => setOpenDownload(false);

  const downloadModel = (runName) => {
    // Only include parameters if they are `true`
    const params = new URLSearchParams();
    const lan = window.localStorage.getItem("languageSet");
    if (includeValidation) params.append("includeValidation", "true");
    if (includeTrain) params.append("includeTrain", "true");
    if (includeOriginal) params.append("includeOriginal", "true");
    if (includeSplit) params.append("includeSplit", "true");
    if (includeLinuxPythonEnv) params.append("includeLinuxDownload", "true");
    if (includeWindowsPythonEnv)
      params.append("includeWindowsDownload", "true");
    params.append("language", lan);

    const url = `${
      process.env.REACT_APP_BACKEND_URL
    }/api/v0/dataset/${encodeURIComponent(
      datasets[currentDatasetIdx].datasetName
    )}/experiment/${encodeURIComponent(
      experiments[currentExperimentIdx].experimentName
    )}/download-model/${encodeURIComponent(runName)}?${params.toString()}`;

    fetchDataBlob(url).then((downloadUrl) => {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `${datasets[currentDatasetIdx].datasetName}_${experiments[currentExperimentIdx].experimentName}_${runName}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      handleCloseDownloadSelecter();
      setDownloadModelClicked(false);
    });
  };
  const handleDownload = () => {
    downloadModel(selectedRun, { includeValidation, includeTrain });
  };
  // delete selected Run
  const handleRunDelete = async () => {
    try {
      await deleteData(
        `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/run/${selectedRun}`
      ).then(async (data) => {
        if (data.status === 200) {
          // close dialog
          setOpen(false);
          // reset run data
          setRunData({
            metrics: {},
            predictionDetails: { classNames: [] },
            params: {},
          });
          fetchRuns();
        }
      });
    } catch (e) {
      console.log(e);
    }
  };
  // fetch runs data by API requests, when the current experiment is gathered
  useEffect(() => {
    if (datasets.length >= 1) {
      fetchRuns();
    }
  }, [experiments, currentExperimentIdx]);

  const handleParentRunSelectorChange = (event) => {
    const runFamilyData = runsFamilyData.find(
      (run) => run.parent.runName === event.target.value
    );
    setSelectedParentRun(event.target.value);
    setChildRunsNames(runFamilyData.children.map((child) => child.runName));
    // set last child run as selected
    const lastChildRun = runFamilyData.children.at(
      runFamilyData.children.length - 1
    );
    fetchRun(lastChildRun.runName);
    setSelectedRun(lastChildRun.runName);
  };

  const handleSelectorChange = (event) => {
    setSelectedRun(event.target.value);
    fetchRun(event.target.value);
  };

  // Functions for the Dialog
  const handleDialogDisplay = () => {
    setOpen(!open);
  };

  // fetch runs data by API requests, when the current experiment is gathered
  useEffect(() => {
    if (datasets.length >= 1 && experiments.length >= 1) {
      setSelectedParentRun("");
      setSelectedRun("");
      setChildRunsNames([]);
      setTargetNames([]);
      setRunData({
        metrics: {},
        predictionDetails: { classNames: [] },
        params: { stepName: "" },
      });
      fetchRuns();
    }
  }, [datasets, experiments, currentDatasetIdx, currentExperimentIdx]);

  return (
    <Grid id="analyzer-container" container>
      {isLoading && (
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            width: "100%",
          }}
        >
          <CircularProgress />
        </Grid>
      )}
      {!isLoading && (
        <Grid
          container
          sx={{
            padding: theme.spacing(8, 8),
            borderRadius: "16px",
            backgroundColor: "white",
          }}
        >
          {/* Dialog to check if the selection shall be deleted */}
          <Dialog
            open={open}
            onClose={handleDialogDisplay}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogContent>
              <Alert
                severity="warning"
                className="abortUploadWarning"
                icon={
                  <WarningAmberIcon
                    sx={{
                      alignItems: "center",
                      width: "48px",
                      height: "48px",
                      color: theme.palette.error.main,
                    }}
                  />
                }
              >
                <AlertTitle>
                  <Typography variant="h4" color="text.secondary">
                    {t("common.Warning")}
                  </Typography>
                </AlertTitle>
                <Typography variant="h5" color="text.tertiary">
                  {t("overview.DeleteRunWarning")}
                </Typography>
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                color="primary"
                component="span"
                size="medium"
                onClick={handleRunDelete}
                autoFocus
              >
                {t("common.Agree")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                component="span"
                size="medium"
                onClick={handleDialogDisplay}
              >
                {" "}
                {t("common.Disagree")}
              </Button>
            </DialogActions>
          </Dialog>
          <Grid item xs={12}>
            <Typography variant="h4" gutterBottom>
              {t("analyzer.PageTitle")}
            </Typography>
          </Grid>
          {currentExperimentProgress?.trained > 0 ? (
            <>
              {/* Dropdowns section - stacked vertically */}
              <Grid item xs={12} sx={{ margin: theme.spacing(3, 0) }}>
                <Grid container spacing={2}>
                  {/* First Dropdown */}
                  <Grid item lg={4} md={6} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>
                        {t("analyzer.SelectedRunDropdown")}
                      </InputLabel>
                      <Select
                        value={selectedParentRun}
                        label={t("analyzer.SelectedRunDropdown")}
                        onChange={handleParentRunSelectorChange}
                      >
                        {runsFamilyData.map((data, index) => (
                          <MenuItem
                            key={`run-selection-${index}`}
                            value={data.parent.runName}
                          >
                            {data.parent.runName}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {t("analyzer.DescriptionSelectRun")}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Second Dropdown */}
                  {childRunsNames.length > 0 && (
                    <Grid item lg={4} md={6} xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>
                          {t("analyzer.SelectedPipelineStepDropdown")}
                        </InputLabel>
                        <Select
                          value={selectedRun}
                          label={t("analyzer.SelectedPipelineStepDropdown")}
                          onChange={handleSelectorChange}
                        >
                          {childRunsNames.map((name, index) => (
                            <MenuItem
                              key={`run-selection-${index}`}
                              value={name}
                            >
                              {name.replace(`${selectedParentRun}_`, "")}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {/* Third Dropdown */}
                  {childRunsNames.length > 0 && (
                    <Grid item lg={4} md={6} xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>
                          {t("analyzer.SelectedTargetDropdown")}
                        </InputLabel>

                        {targetNames.length === 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: 56,
                            }}
                          >
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          <Select
                            value={selectedTarget}
                            label={t("analyzer.SelectedTargetDropdown")}
                            onChange={(e) => setSelectedTarget(e.target.value)}
                          >
                            {targetNames.map((col, index) => (
                              <MenuItem key={`target-col-${index}`} value={col}>
                                {col}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {runData && selectedRun && (
                <Grid item xs={12} sx={{ margin: theme.spacing(2, 0) }}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Go to Pipeline Button */}
                    <Grid item lg={4} md={4} xs={12}>
                      <Link
                        to={`/train/${datasets[currentDatasetIdx].datasetName}/${experiments[currentExperimentIdx].experimentName}/pipeline/${selectedParentRun}`}
                        style={{ textDecoration: "none" }}
                        state={{ fromAnalyzer: true }}
                      >
                        <Button
                          variant="outlined"
                          color="primary"
                          size="medium"
                          startIcon={<QueryStatsIcon />}
                          fullWidth
                        >
                          {t("analyzer.GoToPipelineButton")}
                        </Button>
                      </Link>
                    </Grid>
                    {/* Download Button */}
                    <Grid item lg={4} md={4} xs={12}>
                      <Button
                        onClick={handleOpenDownloadSelecter}
                        variant="outlined"
                        color="primary"
                        disabled={runData.params.stepName.startsWith("AFS")}
                        startIcon={<FileDownloadIcon />}
                        fullWidth
                      >
                        {t("analyzer.DownloadModelButton")}
                      </Button>
                    </Grid>

                    {/* Delete Button */}
                    <Grid item lg={4} md={4} xs={12}>
                      <Button
                        onClick={handleDialogDisplay}
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        fullWidth
                      >
                        {t("analyzer.DeleteRunButton")}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {/* Download Dialog */}
              <Dialog open={openDownload} onClose={handleCloseDownloadSelecter}>
                <DialogTitle variant="h4">
                  {t("analyzer.DownloadOptions")}
                </DialogTitle>
                <DialogContent>
                  <Typography variant="h7">
                    {t("analyzer.DownloadModelDescription")}
                  </Typography>
                  <Box mt={2}>
                    <Typography variant="h5">
                      {t("analyzer.DataDownload")}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeValidation}
                        onChange={() =>
                          setIncludeValidation(!includeValidation)
                        }
                      />
                    }
                    label={t("analyzer.IncludeValidation")}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeTrain}
                        onChange={() => setIncludeTrain(!includeTrain)}
                      />
                    }
                    label={t("analyzer.IncludeTrainData")}
                  />
                  <Box mt={2}>
                    <Typography variant="h5">
                      {t("analyzer.AdvancedDataDownload")}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeOriginal}
                        onChange={() => setIncludeOriginal(!includeOriginal)}
                      />
                    }
                    label={t("analyzer.IncludeOriginalData")}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeSplit}
                        onChange={() => setIncludeSplit(!includeSplit)}
                      />
                    }
                    label={t("analyzer.IncludeSplitData")}
                  />
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.warning.main, 0.08),
                      border: `1px solid ${alpha(
                        theme.palette.warning.main,
                        0.2
                      )}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        pb: 1,
                        borderBottom: `2px solid ${theme.palette.warning.main}`,
                        width: "fit-content",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.warning.main,
                        }}
                      >
                        {t("analyzer.Warning")}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeLinuxPythonEnv}
                          onChange={() =>
                            setIncludeLinuxPythonEnv(!includeLinuxPythonEnv)
                          }
                        />
                      }
                      label={t("analyzer.IncludeLinux")}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeWindowsPythonEnv}
                          onChange={() =>
                            setIncludeWindowsPythonEnv(!includeWindowsPythonEnv)
                          }
                        />
                      }
                      label={t("analyzer.IncludeWindows")}
                    />
                  </Box>
                </DialogContent>

                <DialogActions>
                  <Button
                    onClick={handleCloseDownloadSelecter}
                    color="secondary"
                  >
                    {!downloadModelClicked
                      ? t("analyzer.Cancel")
                      : t("analyzer.CloseWindow")}
                  </Button>

                  {!downloadModelClicked ? (
                    <Button
                      onClick={() => {
                        setDownloadModelClicked(true);
                        handleDownload();
                      }}
                      color="primary"
                      variant="contained"
                      startIcon={<FileDownloadIcon />}
                    >
                      {t("analyzer.DownloadModelButton")}
                    </Button>
                  ) : (
                    <div>
                      <CircularProgress />
                      <Typography variant="body1" component="p">
                        &nbsp;{t("analyzer.CreatingDownloadZip")}
                      </Typography>
                    </div>
                  )}
                </DialogActions>
              </Dialog>

              {runData &&
                selectedRun &&
                (() => {
                  const selectedTargetIndex =
                    targetNames.indexOf(selectedTarget);

                  switch (runData.params.experimentType) {
                    case "Classification":
                      return <AnalyzeClassification runData={runData} />;
                    case "Forecast":
                      return <AnalyzeForecast runData={runData} />;
                    case "Error Model":
                      return (
                        <>
                          <AnalyzeErrorModel
                            runData={runData}
                            runName={selectedRun}
                            selectedTarget={selectedTarget}
                          />
                          <AnalyzeRegression
                            runData={runData}
                            selectedTargetIndex={selectedTargetIndex}
                          />
                        </>
                      );
                    default:
                      return (
                        <AnalyzeRegression
                          runData={runData}
                          selectedTargetIndex={selectedTargetIndex}
                        />
                      );
                  }
                })()}

              {(runData.params.stepName.startsWith("AFS") ||
                runData.params.stepName === "FeatureSelection") &&
                runData.afsDetails && (
                  // if the step is FeatureSelection, show the AFS analysis
                  // the if is to allow legacy runs to display correctly TODO@legacy now it is only AFS
                  <AnalyzeAFS runData={runData} />
                )}
            </>
          ) : (
            // if user lands on this page, without having all required steps fulfilled, show hint
            <Grid item xs={12}>
              {" "}
              <Typography variant="body">
                {t("analyzer.NoExperiments")}
                <Link to="/train">{t("analyzer.NoExperiments_LinkText")}</Link>
                {t("analyzer.NoExperiments_AfterLink")}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  );
}
